// Voice booking button — records a short voice clip via MediaRecorder, sends it
// to the backend Whisper endpoint, and returns extracted booking fields the
// parent component can use to prefill the form. Uses no external dependency
// beyond the browser's built-in audio APIs.
import { useEffect, useRef, useState } from "react";
import { Mic, Square, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";

const MAX_RECORDING_MS = 20_000; // 20s cap — Whisper is fast but we keep UX snappy

export default function VoiceBookingButton({ onFields, lang }) {
  const { t, lang: ctxLang } = useLang();
  const [state, setState] = useState("idle"); // idle | recording | processing | done | error
  const [transcript, setTranscript] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const stopTimerRef = useRef(null);

  const cleanupTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  };

  useEffect(() => () => cleanupTimers(), []);

  const start = async () => {
    setErrorMsg("");
    setTranscript("");
    setSeconds(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
        : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        cleanupTimers();
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        await processAudio(blob, rec.mimeType || "audio/webm");
      };
      recorderRef.current = rec;
      rec.start();
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      stopTimerRef.current = setTimeout(() => stop(), MAX_RECORDING_MS);
    } catch (err) {
      setErrorMsg(err?.message || "Mikrofon-Zugriff verweigert.");
      setState("error");
    }
  };

  const stop = () => {
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    setState("processing");
  };

  const processAudio = async (blob, mime) => {
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const r = await api.post("/voice/booking-intent", {
        audio_base64: b64,
        mime_type: mime,
        lang: lang || ctxLang || "de",
      }, { timeout: 60000 });
      if (r.data?.ok) {
        setTranscript(r.data.transcript || "");
        if (onFields && r.data.fields) onFields(r.data.fields);
        setState("done");
      } else {
        setErrorMsg(r.data?.error || "Keine Sprache erkannt.");
        setState("error");
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err?.message || "Fehler bei Sprachverarbeitung.");
      setState("error");
    }
  };

  const reset = () => {
    setState("idle");
    setTranscript("");
    setErrorMsg("");
    setSeconds(0);
  };

  const labelMap = t.voice || {};

  return (
    <div className="border border-[#FF5A00]/30 bg-gradient-to-br from-orange-50 to-white p-5 mb-6" data-testid="voice-booking-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-[#FF5A00] flex items-center justify-center text-white flex-shrink-0">
          {state === "recording" ? <Square size={18} /> : state === "processing" ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#FF5A00] mb-1">
            {labelMap.title || "Sprach-Buchung · NEU"}
          </div>
          <p className="text-sm text-neutral-700 leading-snug mb-3">
            {labelMap.subtitle || "Sprechen Sie Ihren Auftrag ein — wir füllen das Formular automatisch."}
          </p>

          {state === "idle" && (
            <button
              type="button"
              onClick={start}
              className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition rounded-sm"
              data-testid="voice-booking-start"
            >
              <Mic size={14} /> {labelMap.start || "Aufnahme starten"}
            </button>
          )}

          {state === "recording" && (
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-2 text-red-600 font-bold text-sm" data-testid="voice-booking-recording">
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                {labelMap.recording || "Aufnahme läuft…"} {seconds}s
              </span>
              <button
                type="button"
                onClick={stop}
                className="inline-flex items-center gap-2 border border-neutral-900 hover:bg-neutral-900 hover:text-white font-bold uppercase tracking-wide text-sm px-4 py-2 transition rounded-sm"
                data-testid="voice-booking-stop"
              >
                <Square size={12} /> {labelMap.stop || "Fertig"}
              </button>
            </div>
          )}

          {state === "processing" && (
            <div className="text-sm text-neutral-600 inline-flex items-center gap-2" data-testid="voice-booking-processing">
              <Loader2 size={14} className="animate-spin" /> {labelMap.processing || "Wird transkribiert…"}
            </div>
          )}

          {state === "done" && (
            <div data-testid="voice-booking-done">
              <div className="bg-white border border-neutral-200 p-3 mb-3">
                <div className="text-[10px] font-bold tracking-widest uppercase text-neutral-500 mb-1">{labelMap.transcript || "Transkript"}</div>
                <div className="text-sm text-neutral-800 italic">"{transcript}"</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={reset} className="text-xs font-bold uppercase tracking-wider text-[#FF5A00] hover:underline" data-testid="voice-booking-reset">
                  {labelMap.again || "Erneut versuchen"}
                </button>
              </div>
            </div>
          )}

          {state === "error" && (
            <div className="text-sm text-red-600" data-testid="voice-booking-error">
              {errorMsg}
              <button type="button" onClick={reset} className="ms-3 text-xs font-bold uppercase tracking-wider text-[#FF5A00] hover:underline">
                {labelMap.again || "Erneut versuchen"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
